import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addonAPI, paymentAPI } from '../../services/api';
import { load } from '@cashfreepayments/cashfree-js';

function AddonSubscriptions() {
  const navigate = useNavigate();
  const [availableAddons, setAvailableAddons] = useState([]);
  const [activeAddons, setActiveAddons] = useState([]);
  const [addonRequests, setAddonRequests] = useState([]);
  const [restaurantCity, setRestaurantCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('available');

  useEffect(() => {
    fetchAddons();
  }, []);

  const fetchAddons = async () => {
    try {
      setLoading(true);
      const [availableResponse, myAddonsResponse, requestsResponse] = await Promise.all([
        addonAPI.getAvailableAddons(),
        addonAPI.getMyAddons(),
        addonAPI.getMyAddonRequests()
      ]);

      // Parse responses - backend returns data directly, not nested
      setAvailableAddons(availableResponse.data.addons || []);
      setRestaurantCity(availableResponse.data.restaurantCity || null);
      setActiveAddons(myAddonsResponse.data.addons || []);
      setAddonRequests(requestsResponse.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch addons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (addon, subscriptionType) => {
    if (!window.confirm(`Create ${subscriptionType} subscription request for ${addon.displayName || addon.name}?`)) return;

    try {
      setActionLoading(addon._id);
      const requestData = {
        addonId: addon._id,
        subscriptionType: subscriptionType,
        isGlobal: addon.scope === 'global'
      };

      // Add city info for city-wise addons
      if (addon.scope === 'city_wise' && restaurantCity) {
        requestData.cities = [{
          cityId: restaurantCity._id,
          cityName: restaurantCity.name
        }];
      }

      // Add duration for one-time purchases
      if (subscriptionType === 'one_time' && addon.pricing?.oneTime?.durationDays) {
        requestData.durationDays = addon.pricing.oneTime.durationDays;
      }

      await addonAPI.createAddonRequest(requestData);
      alert('Request created! Please check the Requests tab to complete payment.');
      fetchAddons();
    } catch (error) {
      console.error('Failed to create request:', error);
      alert(error.response?.data?.message || 'Failed to create request. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;

    try {
      setActionLoading(requestId);
      await addonAPI.cancelAddonRequest(requestId);
      alert('Request cancelled successfully!');
      fetchAddons();
    } catch (error) {
      console.error('Failed to cancel request:', error);
      alert(error.response?.data?.message || 'Failed to cancel request.');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePayNow = async (request) => {
    try {
      setPaymentLoading(request._id);

      // Create payment session
      const response = await paymentAPI.createAddonPayment(request._id);
      const { paymentSessionId, cashfreeOrderId, gatewayUrl, useWebView } = response.data.data;

      // Store the order ID for verification
      localStorage.setItem('pendingAddonPaymentOrderId', cashfreeOrderId);
      localStorage.setItem('pendingAddonRequestId', request._id);

      // Check if we should use WebView/redirect flow or SDK
      if (useWebView && gatewayUrl) {
        // Redirect to gateway URL (WebView flow for web)
        window.location.href = gatewayUrl;
      } else {
        // Use Cashfree JS SDK
        const cashfree = await load({
          mode: process.env.REACT_APP_CASHFREE_ENV === 'PROD' ? 'production' : 'sandbox'
        });

        const checkoutOptions = {
          paymentSessionId: paymentSessionId,
          redirectTarget: '_self'
        };

        // This will redirect to the return URL after payment
        // The return URL should be /restaurant/addon-payment-verification
        cashfree.checkout(checkoutOptions);
      }
    } catch (error) {
      console.error('Payment initiation failed:', error);
      alert(error.response?.data?.message || 'Failed to initiate payment. Please try again.');
      setPaymentLoading(null);
    }
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getAddonIcon = (slug) => {
    const icons = {
      'search_priority': '🔍',
      'featured_banner': '⭐',
      'promoted_badge': '✅',
      'priority_delivery': '🚀',
      'default': '📦'
    };
    return icons[slug] || icons.default;
  };

  const getAddonColor = (slug) => {
    const colors = {
      'search_priority': '#3B82F6',
      'featured_banner': '#F59E0B',
      'promoted_badge': '#8B5CF6',
      'priority_delivery': '#10B981',
      'default': '#FF6961'
    };
    return colors[slug] || colors.default;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending_payment': '#F59E0B',
      'payment_completed': '#3B82F6',
      'pending_approval': '#3B82F6',
      'approved': '#10B981',
      'rejected': '#EF4444',
      'cancelled': '#6B7280',
      'active': '#10B981',
      'expired': '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  const getStatusText = (status) => {
    const texts = {
      'pending_payment': 'Pending Payment',
      'payment_completed': 'Payment Completed',
      'pending_approval': 'Awaiting Approval',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'cancelled': 'Cancelled',
      'active': 'Active',
      'expired': 'Expired'
    };
    return texts[status] || status;
  };

  const getEffectivePrice = (addon, type) => {
    const pricing = type === 'monthly' ? addon.pricing?.monthly : addon.pricing?.oneTime;
    if (!pricing) return 0;
    return pricing.effectivePrice ?? pricing.basePrice ?? 0;
  };

  const isAddonActive = (addon) => {
    return activeAddons.some(a => a.addonSlug === addon.slug);
  };

  const hasPendingRequest = (addon) => {
    return addonRequests.some(r =>
      r.addonSlug === addon.slug &&
      ['pending_payment', 'payment_completed', 'pending_approval'].includes(r.status)
    );
  };

  if (loading) {
    return (
      <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          color: '#64748B',
          fontSize: '16px'
        }}>
          Loading add-ons...
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#F8FAFC', minHeight: '100vh' }}>
      <div className="container" style={{ paddingTop: '24px', paddingBottom: '32px' }}>
        {/* Header with Gradient */}
        <div style={{
          background: 'linear-gradient(135deg, #FF6961 0%, #FF8A80 100%)',
          borderRadius: '16px',
          padding: '32px 24px',
          marginBottom: '24px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
          textAlign: 'center'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: 'white',
            marginBottom: '8px',
            letterSpacing: '-0.5px'
          }}>
            Premium Add-ons
          </h1>
          <p style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '15px',
            margin: 0
          }}>
            Boost your restaurant visibility and get more orders
          </p>
          {restaurantCity && (
            <p style={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '13px',
              marginTop: '8px'
            }}>
              📍 Pricing for {restaurantCity.name}
            </p>
          )}
        </div>

        {/* Tab Bar */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          background: 'white',
          padding: '8px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          <button
            onClick={() => setActiveTab('available')}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'available' ? '#FF6961' : 'transparent',
              color: activeTab === 'available' ? 'white' : '#64748B',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <span>🛒</span>
            <span>Available</span>
          </button>
          <button
            onClick={() => setActiveTab('active')}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'active' ? '#FF6961' : 'transparent',
              color: activeTab === 'active' ? 'white' : '#64748B',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <span>✅</span>
            <span>Active ({activeAddons.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === 'requests' ? '#FF6961' : 'transparent',
              color: activeTab === 'requests' ? 'white' : '#64748B',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <span>📋</span>
            <span>Requests ({addonRequests.length})</span>
          </button>
        </div>

        {/* Available Addons Tab */}
        {activeTab === 'available' && (
          <>
            {availableAddons.length === 0 ? (
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '48px 24px',
                textAlign: 'center',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>📦</div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#0F172A',
                  marginBottom: '8px'
                }}>
                  No Add-ons Available
                </h3>
                <p style={{ color: '#64748B', fontSize: '15px', margin: 0 }}>
                  Premium add-ons will appear here when available.
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: '20px'
              }}>
                {availableAddons.map(addon => {
                  const isActive = isAddonActive(addon);
                  const hasPending = hasPendingRequest(addon);
                  const addonColor = getAddonColor(addon.slug);

                  return (
                    <div key={addon._id} style={{
                      background: 'white',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      border: isActive ? `2px solid #10B981` : 'none',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s ease'
                    }}>
                      {/* Header */}
                      <div style={{
                        background: `${addonColor}15`,
                        padding: '20px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '16px'
                      }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '12px',
                          background: `${addonColor}25`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '28px',
                          flexShrink: 0
                        }}>
                          {getAddonIcon(addon.slug)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <h3 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#0F172A',
                            marginBottom: '4px'
                          }}>
                            {addon.displayName || addon.name}
                          </h3>
                          {addon.scope === 'city_wise' && restaurantCity && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '12px',
                              color: '#64748B'
                            }}>
                              <span>📍</span>
                              <span>For {restaurantCity.name}</span>
                            </div>
                          )}
                        </div>
                        {isActive && (
                          <span style={{
                            background: '#10B981',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '700'
                          }}>
                            ACTIVE
                          </span>
                        )}
                        {hasPending && !isActive && (
                          <span style={{
                            background: '#F59E0B',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '700'
                          }}>
                            PENDING
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      {addon.description && (
                        <div style={{ padding: '16px 20px 0 20px' }}>
                          <p style={{
                            color: '#64748B',
                            fontSize: '14px',
                            lineHeight: '1.6',
                            margin: 0
                          }}>
                            {addon.description}
                          </p>
                        </div>
                      )}

                      {/* Pricing */}
                      <div style={{ padding: '16px 20px' }}>
                        {addon.pricing?.monthly?.enabled && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 0',
                            borderBottom: addon.pricing?.oneTime?.enabled ? '1px solid #F1F5F9' : 'none'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '16px' }}>📅</span>
                              <span style={{ color: '#64748B', fontSize: '14px' }}>Monthly</span>
                            </div>
                            <span style={{ fontWeight: '700', color: addonColor, fontSize: '18px' }}>
                              {formatCurrency(getEffectivePrice(addon, 'monthly'))}/mo
                            </span>
                          </div>
                        )}
                        {addon.pricing?.oneTime?.enabled && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 0'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '16px' }}>⚡</span>
                              <span style={{ color: '#64748B', fontSize: '14px' }}>
                                One-Time
                                {addon.pricing.oneTime.durationDays && (
                                  <span style={{ fontSize: '12px' }}> ({addon.pricing.oneTime.durationDays} days)</span>
                                )}
                              </span>
                            </div>
                            <span style={{ fontWeight: '700', color: addonColor, fontSize: '18px' }}>
                              {formatCurrency(getEffectivePrice(addon, 'oneTime'))}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      {!isActive && !hasPending && (
                        <div style={{ padding: '0 20px 20px 20px' }}>
                          <div style={{ display: 'flex', gap: '10px' }}>
                            {addon.pricing?.monthly?.enabled && (
                              <button
                                onClick={() => handleCreateRequest(addon, 'monthly')}
                                disabled={actionLoading === addon._id}
                                style={{
                                  flex: 1,
                                  padding: '12px 16px',
                                  background: actionLoading === addon._id ? '#94A3B8' : addonColor,
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '10px',
                                  fontWeight: '600',
                                  fontSize: '14px',
                                  cursor: actionLoading === addon._id ? 'not-allowed' : 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                {actionLoading === addon._id ? 'Processing...' : 'Get Monthly'}
                              </button>
                            )}
                            {addon.pricing?.oneTime?.enabled && (
                              <button
                                onClick={() => handleCreateRequest(addon, 'one_time')}
                                disabled={actionLoading === addon._id}
                                style={{
                                  flex: 1,
                                  padding: '12px 16px',
                                  background: 'transparent',
                                  color: addonColor,
                                  border: `2px solid ${addonColor}`,
                                  borderRadius: '10px',
                                  fontWeight: '600',
                                  fontSize: '14px',
                                  cursor: actionLoading === addon._id ? 'not-allowed' : 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                {actionLoading === addon._id ? 'Processing...' : 'One-Time'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Active Addons Tab */}
        {activeTab === 'active' && (
          <>
            {activeAddons.length === 0 ? (
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '48px 24px',
                textAlign: 'center',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#0F172A',
                  marginBottom: '8px'
                }}>
                  No Active Add-ons
                </h3>
                <p style={{
                  color: '#64748B',
                  fontSize: '15px',
                  marginBottom: '24px'
                }}>
                  Purchase add-ons from the Available tab to boost your restaurant.
                </p>
                <button
                  onClick={() => setActiveTab('available')}
                  style={{
                    padding: '12px 24px',
                    background: '#FF6961',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(255, 105, 97, 0.2)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Browse Available Add-ons
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {activeAddons.map(addon => {
                  const addonColor = getAddonColor(addon.addonSlug);
                  const endDate = addon.isGlobal
                    ? addon.globalSubscription?.endDate
                    : addon.cities?.[0]?.endDate;
                  const daysRemaining = endDate
                    ? Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24))
                    : null;

                  return (
                    <div key={addon._id} style={{
                      background: 'white',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      border: '2px solid #10B981',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                    }}>
                      {/* Header */}
                      <div style={{
                        background: `${addonColor}15`,
                        padding: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '12px',
                          background: `${addonColor}25`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '28px'
                        }}>
                          {getAddonIcon(addon.addonSlug)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <h3 style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#0F172A',
                            marginBottom: '4px'
                          }}>
                            {addon.addonId?.displayName || addon.addonSlug}
                          </h3>
                          <p style={{ color: '#64748B', fontSize: '13px', margin: 0 }}>
                            {addon.subscriptionType === 'monthly' ? 'Monthly Subscription' : 'One-Time Purchase'}
                          </p>
                        </div>
                        <span style={{
                          background: '#10B981',
                          color: 'white',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '700'
                        }}>
                          ACTIVE
                        </span>
                      </div>

                      {/* Details */}
                      <div style={{ padding: '16px 20px' }}>
                        {endDate && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 0',
                            borderBottom: '1px solid #F1F5F9'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>📅</span>
                              <span style={{ color: '#64748B', fontSize: '14px' }}>Valid until</span>
                            </div>
                            <span style={{ fontWeight: '600', fontSize: '14px' }}>
                              {formatDate(endDate)}
                            </span>
                          </div>
                        )}
                        {daysRemaining !== null && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 0'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>⏰</span>
                              <span style={{ color: '#64748B', fontSize: '14px' }}>Days remaining</span>
                            </div>
                            <span style={{
                              background: daysRemaining <= 7 ? '#FEE2E2' : '#D1FAE5',
                              color: daysRemaining <= 7 ? '#EF4444' : '#10B981',
                              padding: '4px 10px',
                              borderRadius: '8px',
                              fontWeight: '600',
                              fontSize: '13px'
                            }}>
                              {daysRemaining} days
                            </span>
                          </div>
                        )}
                        {!addon.isGlobal && addon.cities?.length > 0 && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 0'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>📍</span>
                              <span style={{ color: '#64748B', fontSize: '14px' }}>Active in</span>
                            </div>
                            <span style={{ fontWeight: '600', fontSize: '14px' }}>
                              {addon.cities.map(c => c.cityName).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <>
            {addonRequests.length === 0 ? (
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '48px 24px',
                textAlign: 'center',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>📋</div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#0F172A',
                  marginBottom: '8px'
                }}>
                  No Requests
                </h3>
                <p style={{ color: '#64748B', fontSize: '15px', margin: 0 }}>
                  Your add-on purchase requests will appear here.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {addonRequests.map(request => {
                  const addonColor = getAddonColor(request.addonSlug);
                  const statusColor = getStatusColor(request.status);

                  return (
                    <div key={request._id} style={{
                      background: 'white',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                    }}>
                      {/* Header */}
                      <div style={{
                        background: `${statusColor}15`,
                        padding: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '10px',
                          background: `${addonColor}25`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px'
                        }}>
                          {getAddonIcon(request.addonSlug)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <h3 style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#0F172A',
                            marginBottom: '4px'
                          }}>
                            {request.addonId?.displayName || request.addonSlug}
                          </h3>
                          <p style={{ color: '#64748B', fontSize: '12px', margin: 0 }}>
                            {request.subscriptionType === 'monthly' ? 'Monthly' : 'One-Time'} • {formatCurrency(request.amount)}
                          </p>
                        </div>
                        <span style={{
                          background: statusColor,
                          color: 'white',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: '700',
                          textTransform: 'uppercase'
                        }}>
                          {getStatusText(request.status)}
                        </span>
                      </div>

                      {/* Details */}
                      <div style={{ padding: '16px 20px' }}>
                        {request.createdAt && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 0'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span>📅</span>
                              <span style={{ color: '#64748B', fontSize: '14px' }}>Requested on</span>
                            </div>
                            <span style={{ fontWeight: '500', fontSize: '14px' }}>
                              {formatDate(request.createdAt)}
                            </span>
                          </div>
                        )}
                        {request.payment?.paymentStatus === 'completed' && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 0',
                            color: '#10B981'
                          }}>
                            <span>✅</span>
                            <span style={{ fontSize: '14px' }}>Payment completed</span>
                          </div>
                        )}
                        {request.review?.reviewNotes && (
                          <div style={{
                            background: '#FEE2E2',
                            padding: '12px',
                            borderRadius: '8px',
                            marginTop: '8px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px'
                          }}>
                            <span style={{ color: '#EF4444' }}>ℹ️</span>
                            <span style={{ color: '#EF4444', fontSize: '13px' }}>
                              {request.review.reviewNotes}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      {request.status === 'pending_payment' && (
                        <div style={{
                          padding: '0 20px 20px 20px',
                          display: 'flex',
                          gap: '10px'
                        }}>
                          <button
                            onClick={() => handleCancelRequest(request._id)}
                            disabled={actionLoading === request._id}
                            style={{
                              flex: 1,
                              padding: '12px 16px',
                              background: 'transparent',
                              color: '#EF4444',
                              border: '2px solid #EF4444',
                              borderRadius: '10px',
                              fontWeight: '600',
                              fontSize: '14px',
                              cursor: actionLoading === request._id ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handlePayNow(request)}
                            disabled={paymentLoading === request._id}
                            style={{
                              flex: 1,
                              padding: '12px 16px',
                              background: paymentLoading === request._id ? '#94A3B8' : '#FF6961',
                              color: 'white',
                              border: 'none',
                              borderRadius: '10px',
                              fontWeight: '600',
                              fontSize: '14px',
                              cursor: paymentLoading === request._id ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {paymentLoading === request._id ? 'Processing...' : 'Pay Now'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AddonSubscriptions;
